<template lang="pug">
.scroller-wrap(@click="$emit('click', $event)" :class="theme")
  //- Before the slides so keyboard users reach the arrows without tabbing through every item first;
  //- they're absolutely positioned, so the DOM order doesn't change where they appear
  ArrowNav.pdp-controls(
    :class="controls !== 'inside' && 'outside'"
    v-if="controls !== false && theme !== 'hero-slider' && hasOverflow"
    :style="arrowOffset ? {bottom: arrowOffset} : {}"
    :modelValue="snap ? slide : scrollPos"
    @update:modelValue="nav($event)"
    :max="snap ? maxSlides : 3"
    :prevLabel="prevLabel"
    :nextLabel="nextLabel"
  )
  .scroller
    //- Focusable so keyboard users can scroll it with the arrow keys, even when the slides have nothing to tab to
    .contents.custom-scroll(
      ref="contents"
      tabindex="0"
      role="region"
      :aria-label="__(label || 'Carousel')"
      v-drag-scroll="{snap: snap}"
      @scroll="scroll($event)"
    )
      slot
</template>
<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted, ref, toRef, useTemplateRef, provide } from 'vue'
import ArrowNav from './ArrowNav.vue'
import { carouselContextKey } from './carousel-context'

provide('inScroller', true)

const loadScript = inject<(src: string) => Promise<void>>('loadScript', async (_script: string) => {})

const props = withDefaults(defineProps<{
  controls?: boolean | string,
  snap?: boolean,
  theme?: string | null,
  arrowOffset?: string,
  defaultPosition?: number,
  onChange?: (index: number) => void,
  label?: string,
  prevLabel?: string,
  nextLabel?: string,
}>(), {
  controls        : true,
  snap            : false,
  theme           : null,
  arrowOffset     : '',
  defaultPosition : 0,
})

const emit = defineEmits(['click', 'change'])

const contentsRef = useTemplateRef<HTMLElement>('contents')

// 0 = far left, 1 = middle, 2 = far right
const scrollPos = ref<0 | 1 | 2>(0)

// When in Snap mode (100% width slides), these variables help determine the current slide
const slide = ref(0)
const maxSlides = ref(0)
const hasOverflow = ref(true)

let checkInterval: any = undefined
let resizeListener: any = undefined
let scrollDebounce: any = undefined

function scrollTo(index: number, smooth = false) {
  index = Math.max(0, Math.min(index, (contentsRef.value?.children.length || 1) - 1))
  const child = contentsRef.value?.children[index] as HTMLElement

  if (child && contentsRef.value) {
    if (smooth) {
      contentsRef.value.scrollTo({ left: child.offsetLeft, behavior: 'smooth' })
    } else {
      contentsRef.value.scrollLeft = child.offsetLeft
    }
  }
}

// Slides render inside our default slot, so they can't take a `scrollTo` prop.
// Hero uses this to draw its own prev/next controls for the `hero-slider` theme.
provide(carouselContextKey, {
  theme    : toRef(props, 'theme'),
  contents : contentsRef,
  scrollTo,
})

function resize() {
  scrollTo(slide.value)
}

function nav(val: number) {
  clearTimeout(scrollDebounce)
  if (props.snap) {
    slide.value = val
    scrollTo(slide.value, true)
  } else {
    if (contentsRef.value) {
      const dir = val > scrollPos.value ? 1 : -1
      contentsRef.value.scrollTo({
        left     : contentsRef.value.scrollLeft + (dir * contentsRef.value.clientWidth),
        behavior : 'smooth',
      })
    }
  }
}

function scroll(e: Event) {
  if ((!props.onChange || !props.snap) && !props.controls) {
    return
  }

  clearTimeout(scrollDebounce)
  scrollDebounce = setTimeout(() => {
    if (!contentsRef.value) {
      return
    }

    if (contentsRef.value.scrollLeft === 0) {
      scrollPos.value = 0
    } else if (contentsRef.value.scrollLeft + contentsRef.value.clientWidth >= contentsRef.value.scrollWidth) {
      scrollPos.value = 2
    } else {
      scrollPos.value = 1
    }

    if (props.snap) {
      const slideWidth = contentsRef.value.children[0].clientWidth
      slide.value = Math.round(contentsRef.value.scrollLeft / slideWidth)
    }

    if (props.onChange) {
      if (props.snap) {
        emit('change', slide.value)
      } else {
        for (let i = 0; i < contentsRef.value.children.length; i += 1) {
          const box = contentsRef.value.children[i].getBoundingClientRect()
          if (box.left > -20 && box.right < window.innerWidth + 20) {
            emit('change', i)
            break
          }
        }
      }
    }
  }, 50)
}

onMounted(() => {
  loadScript('BV')
  nav(props.defaultPosition)
  const updateMaxSlides = () => {
    if (contentsRef.value) {
      hasOverflow.value = contentsRef.value.scrollWidth > contentsRef.value.clientWidth
    }
    if (props.snap && contentsRef.value) {
      maxSlides.value = contentsRef.value.children.length
    }
  }
  checkInterval = setInterval(updateMaxSlides, 1000)

  updateMaxSlides()

  resizeListener = updateMaxSlides
  window.addEventListener('resize', resizeListener)

  if (props.snap) {
    window.addEventListener('resize', resize)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeListener)
  if (props.snap) {
    window.removeEventListener('resize', resize)
  }
  clearInterval(checkInterval)
})

defineExpose({
  scrollTo,
  theme: props.theme,
})
</script>

<style lang="scss" scoped>
.scroller-wrap {
  width: 100%;
  overflow: visible;
  position: relative;
}

.scroller-wrap.hero-slider .custom-scroll {
  padding-bottom: 0;
  overflow: hidden;
}

.scroller-wrap:not(.force-controls) .arrows.outside {
  display: none;
}

.arrows.outside {
  top: 50%;
  height: 32px;
}

@include all-desktop() {
  .scroller-wrap:hover {
    .arrows {
      display: flex;
    }
  }
}

.scroller {
  width: 100%;
  position: relative;
}

.contents {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: $space-xxl;

  > * {
    flex-shrink: 0;
    flex-grow: 0;
  }
}

@supports (-webkit-overflow-scrolling: touch) {
  .contents {
    padding-bottom: 0;
  }
}

@include mobile() {
  .contents {
    padding-bottom: $space-m;
  }
}

.small-pad .contents {
  padding-bottom: $space-m;
}

.no-pad .contents {
  padding-bottom: 0;
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* Internet Explorer 10+ */

  &::-webkit-scrollbar, &::-webkit-scrollbar { /* WebKit */
    width: 0 !important;
    height: 0 !important;
  }

}

// Themes
.scroller-wrap.mobile-overflow {
  @include mobile() {
    margin-right: -$space-s;
    width: auto;
  }
}

</style>
<style lang="scss">

@media (max-width: $tablet) {
  .mobile-full .scroller-wrap {
    padding-left: $space-s;

    .controls {
      .control:first-child {
        margin-left: 0;
      }

      .control:last-child {
        margin-right: $space-s;
      }
    }

    .contents {
      padding-bottom: $space-s;
    }
  }
}
</style>
